import {forkJoin, of, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '@root/src/environments/environment';
import {IndexType} from '@services/satellite-indices.service';
import {SatelliteProductsService} from '@services/satellite-products.service';
import {ElectronicFieldPolygonService} from '@app/pages/satellite-images/services/electronic-field-polygon.service';
import {IndexManager} from './index.manager';
import {MapLayersController} from '../controllers/map-layers.controller';
import {MapControllerInput} from '../interfaces';
import {SatelliteTimelineDate} from '../components/satellite-timeline/satellite-timeline.component';
import {OsmIndexLegendGeoOverlayManager} from './osm-index-legend-geo-overlay.manager';
import {OsmOverlaySourceType} from '../constants/osm-map-baselayer.util';
import {OSM_OVERLAY_TILE_LAYER_OPTIONS} from '../constants/osm-tile-layer-performance.util';

export type SatelliteDisplayMode = 'range' | 'all';

export interface SatelliteTimelineHost {
  destroy$: Subject<void>;
  translate: TranslateService;
  indexManager: IndexManager;
  satelliteProducts: SatelliteProductsService;
  polygonService: ElectronicFieldPolygonService;
  osmIndexOverlay: OsmIndexLegendGeoOverlayManager;
  mapInstance: any;
  layersController: MapLayersController | undefined;
  mapController$: Subject<MapControllerInput>;
  selectedIndexLayer: string | null;
  selectedIndexDate: Date | null;
  currentSatelliteLayer: OsmOverlaySourceType | null;
  satelliteDisplayMode: SatelliteDisplayMode | null;
  pendingAutoSelectLatest: boolean;
  timelineDates: SatelliteTimelineDate[];
  timelineRangeStart: string | null;
  timelineRangeEnd: string | null;
  selectedTimelineDates: string[];
  timelineLoading: boolean;
  timelineLoadedYears: Set<number>;
  timelineLoadingYears: Set<number>;
  timelineCustomLayerIds: Set<string>;
  timelinePolygonIds: Set<string>;
  timelineInitialSelectionDone: boolean;
  satelliteLayerOpacity: number;
  indexLayerVisible: boolean;
  onTimelineSelectionChanged?: () => void;
}

const INDEX_TILE_BASE = `${environment.api}/tiles/Map/GetTile`;
const SATELLITE_PRODUCT_TAKE = 50;
const MIN_TIMELINE_YEAR = 2015;
const INDEX_LAYER_PREFIX = 'timeline-index-';
const SAT_LAYER_PREFIX = 'timeline-sat-';
const INDEX_STAGGER_MS = 300;

export class SatelliteTimelineManager {
  private overlayLoadToken = 0;

  constructor(private readonly getHost: () => SatelliteTimelineHost) {}

  isActive(): boolean {
    const h = this.getHost();
    return !!(h.selectedIndexLayer || h.currentSatelliteLayer === 'sentinel' || h.currentSatelliteLayer === 'landsat');
  }

  setIndexLayersOpacity(opacityPercent: number, visible = true): void {
    const h = this.getHost();
    const pct = Math.max(0, Math.min(100, opacityPercent));
    h.indexManager.rememberIndexTileOpacityPercent(pct);
    h.layersController?.setCustomLayersOpacityByPrefix(INDEX_LAYER_PREFIX, visible ? pct / 100 : 0);
    if (h.selectedTimelineDates.length <= 1) {
      h.indexManager.setCurrentLayerOpacity(visible ? pct : 0, visible);
    }
  }

  setSatelliteLayersOpacity(opacityPercent: number): void {
    const h = this.getHost();
    const pct = Math.max(0, Math.min(100, opacityPercent));
    h.satelliteLayerOpacity = pct;
    h.layersController?.setCustomLayersOpacityByPrefix(SAT_LAYER_PREFIX, pct / 100);
  }

  reloadDates(): void {
    const h = this.getHost();
    this.clearCustomLayers();
    h.indexManager.removeIndexTileLayer();
    h.selectedIndexDate = null;
    h.timelineDates = [];
    h.timelineRangeStart = null;
    h.timelineRangeEnd = null;
    h.selectedTimelineDates = [];
    h.timelineLoadedYears = new Set();
    h.timelineLoadingYears = new Set();
    h.pendingAutoSelectLatest = true;
    h.timelineInitialSelectionDone = false;

    if (!this.isActive()) {
      h.timelineLoading = false;
      h.pendingAutoSelectLatest = false;
      return;
    }
    this.loadYear(new Date().getFullYear());
  }

  loadPreviousYear(): void {
    const h = this.getHost();
    if (!this.isActive() || !h.timelineInitialSelectionDone || !h.timelineLoadedYears.size) {
      return;
    }
    const target = Math.min(...h.timelineLoadedYears) - 1;
    if (target < MIN_TIMELINE_YEAR || h.timelineLoadedYears.has(target) || h.timelineLoadingYears.has(target)) {
      return;
    }
    this.loadYear(target);
  }

  ensureYearLoaded(year: number): void {
    if (this.isActive() && year) {
      this.loadYear(year);
    }
  }

  activateAllMode(): void {
    const h = this.getHost();
    if (h.selectedIndexLayer || (h.currentSatelliteLayer !== 'sentinel' && h.currentSatelliteLayer !== 'landsat')) {
      return;
    }
    h.satelliteDisplayMode = 'all';
    h.timelineRangeStart = null;
    h.timelineRangeEnd = null;
    h.selectedTimelineDates = [];
    this.clearCustomLayers();
    h.onTimelineSelectionChanged?.();
  }

  selectTimelineDate(date: string): void {
    const h = this.getHost();
    if (!date) {
      return;
    }

    if (!h.timelineRangeStart || h.timelineRangeEnd) {
      h.timelineRangeStart = date;
      h.timelineRangeEnd = null;
    } else if (date === h.timelineRangeStart) {
      h.timelineRangeEnd = date;
    } else if (date < h.timelineRangeStart) {
      h.timelineRangeEnd = h.timelineRangeStart;
      h.timelineRangeStart = date;
    } else {
      h.timelineRangeEnd = date;
    }

    this.syncSelectedDatesFromRange();
    this.applySelection();
    h.onTimelineSelectionChanged?.();
    this.focusDate(h.timelineRangeEnd ?? h.timelineRangeStart);
  }

  setRange(start: string | null, end: string | null, notify = true): void {
    const h = this.getHost();
    if (!start) {
      this.clearSelection();
      return;
    }

    const resolvedEnd = end || start;
    if (start <= resolvedEnd) {
      h.timelineRangeStart = start;
      h.timelineRangeEnd = resolvedEnd;
    } else {
      h.timelineRangeStart = resolvedEnd;
      h.timelineRangeEnd = start;
    }

    this.syncSelectedDatesFromRange();
    this.applySelection();
    if (notify) {
      h.onTimelineSelectionChanged?.();
    }
    if (h.timelineRangeEnd) {
      this.focusDate(h.timelineRangeEnd);
    }
  }

  clearSelection(): void {
    const h = this.getHost();
    h.timelineRangeStart = null;
    h.timelineRangeEnd = null;
    h.selectedTimelineDates = [];
    this.applySelection();
    h.onTimelineSelectionChanged?.();
  }

  clearCustomLayers(): void {
    const h = this.getHost();
    for (const id of h.timelineCustomLayerIds) {
      h.mapController$.next({removeCustomLayer: {id}});
      h.layersController?.removeCustomLayer(id);
    }
    h.timelineCustomLayerIds = new Set();
    for (const id of h.timelinePolygonIds) {
      h.layersController?.removeDynamicPolygon(id);
      h.mapController$.next({removePolygon: {id}});
    }
    h.timelinePolygonIds = new Set();
    for (const entry of h.layersController?.getAllPolygons() ?? []) {
      const id = String(entry.properties?.id ?? '');
      if (id.startsWith('timeline-sat-border-')) {
        h.layersController?.removeDynamicPolygon(id);
      }
    }
  }

  private loadYear(year: number): void {
    const h = this.getHost();
    if (h.timelineLoadedYears.has(year) || h.timelineLoadingYears.has(year)) {
      return;
    }
    h.timelineLoadingYears.add(year);
    h.timelineLoading = true;

    if (h.selectedIndexLayer) {
      h.indexManager
        .GetIndexDatesFromTileLayers(h.selectedIndexLayer as IndexType, year)
        .pipe(takeUntil(h.destroy$))
        .subscribe({
          next: (dates) => this.mergeEntries(year, (dates || []).map((d) => ({date: this.toIso(d)}))),
          error: () => this.finishYear(year),
        });
      return;
    }

    if (h.currentSatelliteLayer === 'sentinel' || h.currentSatelliteLayer === 'landsat') {
      const satelliteType = h.currentSatelliteLayer === 'landsat' ? 2 : 1;
      h.satelliteProducts
        .customGet({
          offset: 0,
          take: SATELLITE_PRODUCT_TAKE,
          // sortBy: 'CreateAt',
          sort: -1,
          satelliteType,
          startDate: `${year}-01-01`,
          endDate: `${year}-12-31`,
        })
        .pipe(takeUntil(h.destroy$))
        .subscribe({
          next: (resp) => {
            const products: any[] = Array.isArray(resp) ? resp : resp?.data ?? [];
            this.mergeEntries(
              year,
              products.map((p) => this.productToEntry(p)).filter((e) => !!e?.date),
            );
          },
          error: () => this.finishYear(year),
        });
      return;
    }

    h.timelineLoadingYears.delete(year);
    h.timelineLoading = false;
  }

  private productToEntry(product: any): SatelliteTimelineDate {
    const iso = this.toIso(product?.originDate || product?.sensing_time || product?.createAt);
    const cloud = product?.cloudCover ?? product?.cloudCoverPercentage ?? product?.maxCloudCover;
    return {
      date: iso,
      productId: product?.id,
      hasClouds: typeof cloud === 'number' && cloud > 20,
      raw: product,
    };
  }

  private mergeEntries(year: number, entries: SatelliteTimelineDate[]): void {
    const h = this.getHost();
    const previousFirst = h.timelineDates[0]?.date ?? null;
    const byDate = new Map(h.timelineDates.map((e) => [e.date, e]));
    for (const entry of entries) {
      if (entry.date) {
        byDate.set(entry.date, entry);
      }
    }
    h.timelineDates = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
    h.timelineLoadedYears.add(year);

    const prepended = !!(previousFirst && h.timelineDates[0]?.date && h.timelineDates[0].date < previousFirst);
    this.finishYear(year, prepended);

    if (
      !h.selectedIndexLayer
      && h.satelliteDisplayMode === 'range'
      && !h.pendingAutoSelectLatest
      && !prepended
    ) {
      this.applySelection();
    }
  }

  private finishYear(year: number, prependedOlderDates = false): void {
    const h = this.getHost();
    h.timelineLoadingYears.delete(year);
    h.timelineLoadedYears.add(year);
    h.timelineLoading = h.timelineLoadingYears.size > 0;

    if (!h.timelineLoading && h.pendingAutoSelectLatest && !prependedOlderDates) {
      h.pendingAutoSelectLatest = false;
      h.timelineInitialSelectionDone = true;
      if (!h.timelineDates.length) {
        return;
      }
      if (!h.selectedIndexLayer && h.satelliteDisplayMode === 'all') {
        h.onTimelineSelectionChanged?.();
        return;
      }
      const latest = h.timelineDates[h.timelineDates.length - 1].date;
      this.setRange(latest, latest, !h.selectedIndexLayer && h.satelliteDisplayMode === 'range');
    }
  }

  private syncSelectedDatesFromRange(): void {
    const h = this.getHost();
    if (!h.timelineRangeStart) {
      h.selectedTimelineDates = [];
      return;
    }
    const end = h.timelineRangeEnd ?? h.timelineRangeStart;
    const min = h.timelineRangeStart <= end ? h.timelineRangeStart : end;
    const max = h.timelineRangeStart <= end ? end : h.timelineRangeStart;
    h.selectedTimelineDates = h.timelineDates.filter((e) => e.date >= min && e.date <= max).map((e) => e.date);
  }

  private applySelection(): void {
    const token = ++this.overlayLoadToken;
    this.clearCustomLayers();
    const h = this.getHost();

    if (h.selectedIndexLayer) {
      const selected = h.selectedTimelineDates;
      if (!selected.length) {
        h.selectedIndexDate = null;
        h.osmIndexOverlay.onIndexDateChange();
        return;
      }
      h.selectedIndexDate = this.parseDate(selected[selected.length - 1]);
      if (selected.length === 1) {
        h.osmIndexOverlay.onIndexDateChange();
        return;
      }
      h.indexManager.removeIndexTileLayer();
      h.osmIndexOverlay.updateLegend(h.selectedIndexLayer);
      this.resolveIndexGeoServerNames(selected)
        .pipe(takeUntil(h.destroy$))
        .subscribe((names) => {
          if (token !== this.overlayLoadToken || !names.length) {
            return;
          }
          const ordered = [...names].sort((a, b) => this.dateFromGeoName(a).localeCompare(this.dateFromGeoName(b)));
          [...ordered].reverse().forEach((name, i) => {
            setTimeout(() => token === this.overlayLoadToken && this.addTileLayer(INDEX_LAYER_PREFIX, name, name, 'index-tile-layer', h.indexManager.getIndexTileOpacityPercent()), i * INDEX_STAGGER_MS);
          });
        });
      return;
    }

    if (h.satelliteDisplayMode === 'all') {
      h.timelineRangeStart = null;
      h.timelineRangeEnd = null;
      h.selectedTimelineDates = [];
      return;
    }
    if (!h.selectedTimelineDates.length) {
      return;
    }

    const productIds = h.selectedTimelineDates
      .map((date) => h.timelineDates.find((e) => e.date === date))
      .filter((e) => e?.productId)
      .map((e) => e!.productId!);

    for (const date of h.selectedTimelineDates) {
      const entry = h.timelineDates.find((e) => e.date === date);
      if (entry?.productId) {
        this.addProductBorder(entry.productId, entry.raw);
      }
    }

    const dateById = new Map(h.timelineDates.filter((e) => e.productId).map((e) => [e.productId!, e.date]));
    const sorted = [...productIds].sort((a, b) => (dateById.get(b) ?? '').localeCompare(dateById.get(a) ?? ''));
    for (const productId of sorted) {
      if (token !== this.overlayLoadToken) {
        return;
      }
      this.addTileLayer(SAT_LAYER_PREFIX, productId, productId, 'satellite-tile-layer', h.satelliteLayerOpacity ?? 100);
    }
  }

  private resolveIndexGeoServerNames(dates: string[]) {
    const h = this.getHost();
    const indexType = h.selectedIndexLayer;
    if (!indexType || !dates.length) {
      return of([] as string[]);
    }
    return forkJoin(
      dates.map((date) => {
        const parsed = this.parseDate(date);
        if (!parsed) {
          return of(null as string | null);
        }
        return h.indexManager.loadAvailableIndices(parsed, indexType).pipe(
          map((layers: any[]) => layers?.[0]?.geoServerName ?? null),
        );
      }),
    ).pipe(map((names) => names.filter((name): name is string => !!name)));
  }

  private addTileLayer(prefix: string, id: string, layerKey: string, className: string, opacityPct: number): void {
    const h = this.getHost();
    if (!h.layersController) {
      return;
    }
    const layerId = `${prefix}${id}`;
    if (h.layersController.hasCustomLayer(layerId)) {
      return;
    }
    const opacity = Math.max(0, Math.min(100, opacityPct)) / 100;
    const urlFn = prefix === INDEX_LAYER_PREFIX
      ? (coords: {x: number; y: number; z: number}) => this.indexTileUrl(layerKey, coords)
      : (coords: {x: number; y: number; z: number}) => this.satelliteTileUrl(layerKey, coords);
    const layer = h.layersController.addCustomTileLayer(layerId, urlFn, {...OSM_OVERLAY_TILE_LAYER_OPTIONS, opacity, className});
    h.timelineCustomLayerIds.add(layerId);
    layer?.redraw?.();
  }

  private addProductBorder(productId: string, raw?: unknown): void {
    const h = this.getHost();
    const coords = this.productCoords(raw);
    if (!coords.length) {
      return;
    }
    const polygonId = `timeline-sat-border-${productId}`;
    h.mapController$.next({
      addPolygon: {
        coordinates: coords,
        properties: {
          id: polygonId,
          type: 'satellite',
          className: 'satellite-image-polygon',
          interactive: false,
          strokeColor: '#FF5722',
          strokeWidth: 3,
          opacity: 1,
          fillColor: 'transparent',
          fillOpacity: 0,
          fill: false,
          dashArray: '10, 5',
        },
      },
    });
    h.timelinePolygonIds.add(polygonId);
  }

  private focusDate(date: string | null): void {
    if (!date) {
      return;
    }
    const h = this.getHost();
    if (h.selectedIndexLayer || (h.currentSatelliteLayer !== 'sentinel' && h.currentSatelliteLayer !== 'landsat')) {
      return;
    }
    const product: any = h.timelineDates.find((e) => e.date === date)?.raw;
    const center = product?.center;
    if (center?.lat != null && center?.lng != null) {
      h.mapController$.next({setCenter: {lat: center.lat, lng: center.lng, zoom: 8}});
      return;
    }
    const coords = this.productCoords(product);
    if (coords.length) {
      h.mapController$.next({
        setCenter: {
          lat: coords.reduce((s, c) => s + c[0], 0) / coords.length,
          lng: coords.reduce((s, c) => s + c[1], 0) / coords.length,
          zoom: 8,
        },
      });
    }
  }

  private productCoords(raw: unknown): [number, number][] {
    const h = this.getHost();
    const polygonData = (raw as any)?.polygonJson || (raw as any)?.polygon;
    if (!polygonData) {
      return [];
    }
    const parsed = h.polygonService.parsePolygonData(polygonData);
    const valid = parsed?.length ? h.polygonService.validateCoordinates(parsed) : null;
    return valid?.length ? h.polygonService.toLeafletCoords(valid) : [];
  }

  private indexTileUrl(geoServerName: string, coords: {x: number; y: number; z: number}): string {
    const {lat, lon, z} = this.tileToLatLon(coords);
    const matched = /^([A-Za-z]+)(\d+)$/.exec(geoServerName);
    const layer = matched?.[1] ? `indexint8:${geoServerName}` : geoServerName;
    let url = `${INDEX_TILE_BASE}?lat=${lat}&lon=${lon}&z=${z}&layer=${layer}&coordinateSystem=0`;
    if (matched?.[1]) {
      url += `&style=${matched[1]}_int8`;
    }
    return url;
  }

  private satelliteTileUrl(productId: string, coords: {x: number; y: number; z: number}): string {
    const {lat, lon, z} = this.tileToLatLon(coords);
    return `${INDEX_TILE_BASE}?lat=${lat}&lon=${lon}&z=${z}&layer=satellite:satellite_product_${productId}&coordinateSystem=0`;
  }

  private dateFromGeoName(name: string): string {
    return /^[A-Za-z]+(\d{8})$/.exec(name)?.[1] ?? name;
  }

  private tileToLatLon(coords: {x: number; y: number; z: number}): {lat: number; lon: number; z: number} {
    const n = Math.PI - (2 * Math.PI * (coords.y + 0.5)) / 2 ** coords.z;
    return {
      lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
      lon: ((coords.x + 0.5) / 2 ** coords.z) * 360 - 180,
      z: coords.z,
    };
  }

  private parseDate(iso: string): Date | null {
    if (!iso) {
      return null;
    }
    const parts = iso.split('-').map(Number);
    if (parts.length === 3 && !parts.some(Number.isNaN)) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private toIso(value: unknown): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date) {
      return this.formatIso(value);
    }
    const str = String(value);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
    if (m) {
      return `${m[1]}-${m[2]}-${m[3]}`;
    }
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? '' : this.formatIso(d);
  }

  private formatIso(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
